import { resolveUuidAlias } from '../offline/db';

const STORAGE_KEY = 'nlims_market_trad_shared_records';

function getStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readStore() {
  const storage = getStorage();
  if (!storage) return {};

  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Best-effort cache only.
  }
}

function cloneRecord(record) {
  return record && typeof record === 'object' ? { ...record } : record;
}

function canonicalRecordKey(record) {
  const rawKey = String(record?.uuid ?? record?.id ?? '').trim();
  if (!rawKey) return '';
  return resolveUuidAlias(rawKey) ?? rawKey;
}

function normalizeBucket(records = []) {
  const deduped = new Map();

  records.forEach((record) => {
    if (!record || typeof record !== 'object') return;
    const key = canonicalRecordKey(record);
    if (!key) return;
    deduped.set(key, cloneRecord(record));
  });

  return Array.from(deduped.values());
}

export function getSharedRecords(resourceKey) {
  const store = readStore();
  const bucket = Array.isArray(store[resourceKey]) ? store[resourceKey] : [];
  return bucket.map(cloneRecord);
}

export function replaceSharedRecords(resourceKey, records = []) {
  const store = readStore();
  store[resourceKey] = normalizeBucket(records);
  writeStore(store);
  return getSharedRecords(resourceKey);
}

export function upsertSharedRecords(resourceKey, records = []) {
  const store = readStore();
  const existing = Array.isArray(store[resourceKey]) ? store[resourceKey] : [];
  const next = new Map(
    normalizeBucket(existing).map((record) => [String(record.uuid ?? record.id), cloneRecord(record)])
  );

  records.forEach((record) => {
    if (!record || typeof record !== 'object') return;
    const key = String(record.uuid ?? record.id ?? '').trim();
    if (!key) return;
    next.set(key, cloneRecord(record));
  });

  store[resourceKey] = Array.from(next.values());
  writeStore(store);
  return getSharedRecords(resourceKey);
}

export function upsertSharedRecord(resourceKey, record) {
  if (!record || typeof record !== 'object') return getSharedRecords(resourceKey);
  return upsertSharedRecords(resourceKey, [record]);
}

export function removeSharedRecord(resourceKey, uuid) {
  const key = canonicalRecordKey({ uuid });
  if (!key) return getSharedRecords(resourceKey);

  const store = readStore();
  const existing = Array.isArray(store[resourceKey]) ? store[resourceKey] : [];
  store[resourceKey] = existing.filter((record) => canonicalRecordKey(record) !== key);
  writeStore(store);
  return getSharedRecords(resourceKey);
}

export function mergeRecordsByUuid(primary = [], secondary = []) {
  const merged = new Map();
  let syntheticIndex = 0;

  const pushRecord = (record) => {
    if (!record || typeof record !== 'object') return;
    const key = canonicalRecordKey(record);
    const cacheKey = key || `__synthetic_${syntheticIndex += 1}`;
    if (!merged.has(cacheKey)) {
      merged.set(cacheKey, cloneRecord(record));
    }
  };

  primary.forEach(pushRecord);
  secondary.forEach(pushRecord);

  return Array.from(merged.values());
}
